/**
 * ListSpecs (UI contract rule 10; ADR-013 §2.3-2.6), one per module, built from
 * web/module-ui.json — the per-module design the owner answered on the kit's arch-map
 * (docs/design/ui-contract). Columns and labels come from the descriptors; this file adds no
 * per-module code. Status columns read the kernel column (the descriptor field name), even
 * where the coreBOS list view names it differently (stato_richiesta -> stato).
 */
import type { EntityListSpec, FilterField, QuickFilter, RowAction, WritePosture } from "@evo-flywheel/ui";
import type { FieldDef } from "@evo-flywheel/contracts";
import design from "../module-ui.json";
import { kitResources } from "./kit-resources";

/** Fleet palette (UI contract rule 10). */
const TONES: Record<string, string> = {
  navy: "#282a64", orange: "#f57526", green: "#389e0d", red: "#cf1322", grey: "#8b8db0",
};

/** coreBOS list-view names the kernel stores under the descriptor field name. */
const KERNEL_NAME: Record<string, string> = {
  stato_utente: "stato", stato_richiesta: "stato", stato_prompt: "stato", stato_corso: "stato",
  stato_voce: "stato", esito_attestato: "esito", esito_revisione: "esito",
};

interface ModuleUi {
  status?: { field: string; tones: Record<string, string>; labels?: Record<string, string> };
  quick: { name: string; field: string; op: string; value: unknown };
  posture: WritePosture;
  editable?: string[];
  panel: string;
}

const MODULES = (design as { modules: Record<string, ModuleUi> }).modules;

/** "$days:-7" -> the ISO date seven days ago, resolved when the app loads. */
const resolve = (v: unknown): unknown => {
  const m = typeof v === "string" ? /^\$days:(-?\d+)$/.exec(v) : null;
  return m ? new Date(Date.now() + Number(m[1]) * 86_400_000).toISOString().slice(0, 10) : v;
};

/** Row actions per posture. Edit and delete go through the kit's own pages (commands). */
const ROW_ACTIONS: Record<WritePosture, RowAction[]> = {
  crud: [
    { kind: "drill", label: "Apri" },
    { kind: "command", command: "edit", label: "Modifica", icon: "EditOutlined" },
    { kind: "command", command: "delete", label: "Elimina", icon: "DeleteOutlined", danger: true },
  ],
  // Spine edits wait on a kernel write route (backlog C53): drill only until it exists.
  whitelist: [{ kind: "drill", label: "Apri" }],
  readonly: [{ kind: "drill", label: "Apri" }],
};

function buildSpec(slug: string, ui: ModuleUi): EntityListSpec {
  const meta = kitResources.find((r) => r.name === slug)?.meta;
  const fields = new Map<string, FieldDef>((meta?.fields ?? []).map((f) => [f.name, f]));
  const statusField = ui.status?.field;
  const names = [...new Set((meta?.listView ?? []).map((n) => KERNEL_NAME[n] ?? n))]
    .filter((n) => fields.get(n)?.type !== "json");
  if (statusField && !names.includes(statusField)) names.splice(1, 0, statusField);

  const columns = names.map((n) => {
    const f = fields.get(n);
    if (n === statusField) return { dataIndex: n, title: f?.label ?? "Stato", width: 150, renderer: "statusTag" as const };
    if (f?.type === "datetime" || f?.type === "date") return { dataIndex: n, title: f.label ?? n, width: 150, renderer: "datetime" as const };
    return { dataIndex: n, title: f?.label ?? n, ellipsis: true, width: n === names[0] ? 280 : 170 };
  });

  const filters: FilterField[] = [];
  if (ui.status) {
    filters.push({
      name: ui.status.field, label: fields.get(ui.status.field)?.label ?? "Stato", type: "picklist",
      options: Object.keys(ui.status.tones).map((v) => ({ value: v, label: ui.status!.labels?.[v] ?? v })),
    });
  }
  for (const n of names) {
    const f = fields.get(n);
    if (f?.type === "date" || f?.type === "datetime") filters.push({ name: n, label: f.label ?? n, type: f.type });
    if (f?.type === "boolean") filters.push({ name: n, label: f.label ?? n, type: "boolean" });
  }

  const quick: QuickFilter = {
    id: `${slug}-primary`, name: ui.quick.name,
    filters: [{ field: ui.quick.field, operator: ui.quick.op, value: resolve(ui.quick.value) }],
  };

  return {
    kind: "entity",
    rowKey: "id",
    searchField: "q",
    searchPlaceholder: `Cerca ${(meta?.label ?? slug).toLowerCase()}…`,
    writePosture: ui.posture,
    editableColumns: ui.editable,
    statusColors: ui.status ? Object.fromEntries(Object.entries(ui.status.tones).map(([v, t]) => [v, TONES[t] ?? t])) : undefined,
    statusLabels: ui.status?.labels,
    columns,
    filters,
    quickFilters: [quick],
    rowActions: ROW_ACTIONS[ui.posture],
    massActions: [{ kind: "export-csv", label: "Esporta CSV" }],
  };
}

export const specs: Record<string, EntityListSpec> = Object.fromEntries(
  Object.entries(MODULES).map(([slug, ui]) => [slug, buildSpec(slug, ui)]),
);

/** Declared vertical-panel use per module (ADR-013 §2.6): "partial: …", "full: …" or "none". */
export const panelUse: Record<string, string> = Object.fromEntries(
  Object.entries(MODULES).map(([slug, ui]) => [slug, ui.panel]),
);
